import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Instagram, Facebook, Youtube, Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { BRAND_LOGO } from "@/components/SEO";
import api from "@/lib/api";

export default function Footer() {
  const settings = useSettings();
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  const [dests, setDests] = useState([]);
  useEffect(() => { api.get("/destinations").then((r) => setDests(r.data)).catch(() => {}); }, []);
  const india = dests.filter((d) => d.region === "India").sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  const intl = dests.filter((d) => d.region === "International").sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  const waNumber = (settings?.whatsapp || "").replace(/\D/g, "");
  const waLink = `https://wa.me/${waNumber}?text=${encodeURIComponent("Hi Happy Holidays Wale, I'd like to enquire about a tour.")}`;

  return (
    <footer className="bg-navy text-white" style={{ backgroundColor: "#002244" }}>
      {/* CTA band — home page only, no background box */}
      {isHome && (
        <div className="mx-auto max-w-7xl px-4 pt-14 text-center md:px-8" data-testid="footer-cta">
          <h2 className="font-display text-3xl font-extrabold sm:text-4xl">Ready to start your journey?</h2>
          <p className="mx-auto mt-3 max-w-xl text-white/60">Tell us where you want to go and our travel experts will take care of the rest.</p>
          <div className="mt-7 flex flex-wrap justify-center gap-4">
            <Link to="/enquiry" data-testid="footer-enquire-btn" className="btn-gradient rounded-full px-8 py-3.5 font-bold transition-transform hover:scale-105">Enquire Now</Link>
            <a href={waLink} target="_blank" rel="noreferrer" data-testid="footer-whatsapp-btn" className="flex items-center gap-2 rounded-full bg-[#25D366] px-8 py-3.5 font-bold text-white transition-transform hover:scale-105"><MessageCircle className="h-5 w-5" /> WhatsApp Us</a>
          </div>
        </div>
      )}

      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 md:grid-cols-4 md:px-8">
        <div>
          <div className="mb-4 inline-flex rounded-2xl bg-white p-3"><img src={settings?.logo || BRAND_LOGO} alt="Happy Holidays Wale" className="h-10 w-auto" /></div>
          <p className="text-sm leading-relaxed text-white/70">{settings?.footer_text}</p>
          <div className="mt-5 flex gap-3">
            <a href={settings?.social?.instagram || "#"} className="rounded-full bg-white/10 p-2.5 transition-colors hover:bg-brandorange"><Instagram className="h-5 w-5" /></a>
            <a href={settings?.social?.facebook || "#"} className="rounded-full bg-white/10 p-2.5 transition-colors hover:bg-brandorange"><Facebook className="h-5 w-5" /></a>
            <a href={settings?.social?.youtube || "#"} className="rounded-full bg-white/10 p-2.5 transition-colors hover:bg-brandorange"><Youtube className="h-5 w-5" /></a>
          </div>
        </div>

        <div>
          <h4 className="mb-4 font-display text-lg font-bold">India</h4>
          <ul className="space-y-2.5 text-sm text-white/70">
            {india.map((d) => <li key={d.id}><Link to={`/tours?region=India&destination=${d.slug}`} className="hover:text-brandorange">{d.name} Tour Packages</Link></li>)}
          </ul>
        </div>

        <div>
          <h4 className="mb-4 font-display text-lg font-bold">International</h4>
          <ul className="space-y-2.5 text-sm text-white/70">
            {intl.map((d) => <li key={d.id}><Link to={`/tours?region=International&destination=${d.slug}`} className="hover:text-brandorange">{d.name} Tour Packages</Link></li>)}
          </ul>
        </div>

        <div>
          <h4 className="mb-4 font-display text-lg font-bold">Get in Touch</h4>
          <ul className="space-y-3 text-sm text-white/70">
            <li className="flex items-start gap-3"><Phone className="mt-0.5 h-4 w-4 text-brandorange" /> {settings?.phone}</li>
            <li className="flex items-start gap-3"><Mail className="mt-0.5 h-4 w-4 text-brandorange" /> {settings?.email}</li>
            <li className="flex items-start gap-3"><MapPin className="mt-0.5 h-4 w-4 text-brandorange" /> {settings?.address}</li>
          </ul>
          <div className="mt-5 flex gap-4 text-sm text-white/70">
            <Link to="/privacy-policy" className="hover:text-brandorange">Privacy</Link>
            <Link to="/terms" className="hover:text-brandorange">Terms</Link>
            <Link to="/blog" className="hover:text-brandorange">Blog</Link>
          </div>
        </div>
      </div>
      <div className="border-t border-white/10 py-5 text-center text-sm text-white/60">
        <p className="font-display text-base font-bold text-white">Happy Holidays Wale</p>
        <p className="mt-2 text-xs text-white/40">© {new Date().getFullYear()} Happy Holidays Wale. All rights reserved.</p>
      </div>
    </footer>
  );
}
